import type { StateCreator } from "zustand";

import type { ZGlobalCreateOpportunity } from "./types";

export const globalCreateOpportunitysSlice: StateCreator<
  ZGlobalCreateOpportunity
> = (set, get) => ({
  isOpenGlobalCreateOpportunity: false,
  setIsOpenGlobalCreateOpportunity: isOpenGlobalCreateOpportunity => {
    set({ isOpenGlobalCreateOpportunity });
  },

  pipelines: [],
  setPipelines: pipelines => {
    set({ pipelines });
  },
  isPipelinesLoading: false,
  setIsPipelinesLoading: isPipelinesLoading => {
    set({ isPipelinesLoading });
  },
  selectedPipeline: null,
  setSelectedPipeline: selectedPipeline => {
    set({ selectedPipeline });
  },

  isSaveAndNewMode: false,
  setIsSaveAndNewMode: isSaveAndNewMode => {
    set({ isSaveAndNewMode });
  },

  isConfirmClearForm: false,
  setIsConfirmClearForm: isConfirmClearForm => {
    set({ isConfirmClearForm });
  },

  isConfirmCloseForm: false,
  setIsConfirmCloseForm: isConfirmCloseForm => {
    set({ isConfirmCloseForm });
  },

  isOpportunityCreating: false,
  setIsOpportunityCreating: isOpportunityCreating => {
    set({ isOpportunityCreating });
  },

  opportunityAttributes: [],
  setOpportunityAttributes: opportunityAttributes => {
    set({ opportunityAttributes });
  },

  isAdditionalFieldsOpen: false,
  setIsAdditionalFieldsOpen: isAdditionalFieldsOpen => {
    set({ isAdditionalFieldsOpen });
  },

  accounts: [],
  setAccounts: accounts => {
    set({ accounts });
  },

  selectedAccount: null,
  setSelectedAccount: selectedAccount => {
    const accounts = get().accounts;

    // First, set all accounts to inactive
    const updatedAccounts = accounts.map(account => ({
      ...account,
      active: false,
    }));

    // Then set the selected account to active
    const index = updatedAccounts.findIndex(
      account => account.id === selectedAccount.id,
    );

    if (index !== -1) {
      updatedAccounts[index] = {
        ...updatedAccounts[index],
        active: true,
      };
    }

    set({ accounts: updatedAccounts });
    set({ selectedAccount });
  },

  addingNewAccount: false,
  setAddingNewAccount: addingNewAccount => {
    set({ addingNewAccount });
  },

  selectTypeAttributes: [],
  setSelectTypeAttributes: selectTypeAttributes => {
    set({ selectTypeAttributes });
  },

  setSelectActiveAttributes: (selectActiveAttribute: {
    property_name: string;
    optionId: string;
  }) => {
    const selectTypeAttributes = get().selectTypeAttributes;

    const updatedSelectTypeAttributes = selectTypeAttributes.map(attribute => {
      // Check if this attribute's property_name matches
      if (attribute.property_name === selectActiveAttribute.property_name) {
        // If property_name matches, update options array
        return {
          ...attribute,
          options: attribute.options.map(option => ({
            ...option,
            // Set active true only if optionId matches
            active: option.id === selectActiveAttribute.optionId,
          })),
        };
      }

      // If property_name doesn't match, return attribute unchanged
      return attribute;
    });

    set({ selectTypeAttributes: updatedSelectTypeAttributes });
  },

  setMultiSelectActiveAttributes: (multiSelectActiveAttributes: {
    property_name: string;
    optionId: string;
  }) => {
    const selectTypeAttributes = get().selectTypeAttributes;

    const updatedSelectTypeAttributes = selectTypeAttributes.map(attribute => {
      // Check if property_name matches
      if (
        attribute.property_name === multiSelectActiveAttributes.property_name
      ) {
        return {
          ...attribute,
          options: attribute.options.map(option => {
            // Only modify the matching option
            if (option.id === multiSelectActiveAttributes.optionId) {
              return {
                ...option,
                // Toggle the active state
                active: !option.active,
              };
            }
            // Return other options unchanged
            return option;
          }),
        };
      }
      // Return other attributes unchanged
      return attribute;
    });

    set({ selectTypeAttributes: updatedSelectTypeAttributes });
  },

  contacts: [],
  setContacts: contacts => {
    set({ contacts });
  },

  selectedContact: null,
  setSelectedContact: selectedContact => {
    const contacts = get().contacts;

    // First, set all contacts to inactive
    const updatedContacts = contacts.map(contact => ({
      ...contact,
      active: false,
    }));

    // Then set the selected account to active
    const index = updatedContacts.findIndex(
      contact => contact.id === selectedContact.id,
    );

    if (index !== -1) {
      updatedContacts[index] = {
        ...updatedContacts[index],
        active: true,
      };
    }

    set({ contacts: updatedContacts });
    set({ selectedContact });
  },

  multiSelectContacts: [],
  setMultiSelectContacts: multiSelectContacts => {
    set({ multiSelectContacts });
  },

  selectedMultiSelectContact: null,
  setSelectedMultiSelectContact: selectedMultiSelectContact => {
    const currentContacts = get().multiSelectContacts || [];

    const updatedContacts = currentContacts.map(contact => {
      if (contact.id === selectedMultiSelectContact.id) {
        // Toggle active state if contact exists
        return { ...contact, active: !contact.active };
      }
      return contact;
    });

    set({ multiSelectContacts: updatedContacts });
  },

  stages: [],
  setStages: stages => {
    set({ stages });
  },

  selectedStage: null,
  setSelectedStage: selectedStage => {
    const stages = get().stages;

    // First, set all contacts to inactive
    const updatedStages = stages.map(stage => ({
      ...stage,
      active: false,
    }));

    // Then set the selected account to active
    const index = updatedStages.findIndex(
      stage => stage.id === selectedStage?.id,
    );

    if (index !== -1) {
      updatedStages[index] = {
        ...updatedStages[index],
        active: true,
      };
    }

    set({ stages: updatedStages });
    set({ selectedStage });
  },

  teamMembers: [],
  setTeamMembers: teamMembers => {
    set({ teamMembers });
  },

  selectedTeamMember: null,
  setSelectedTeamMember: selectedTeamMember => {
    const teamMembers = get().teamMembers;

    // First, set all team members to inactive
    const updatedTeamMembers = teamMembers.map(teamMember => ({
      ...teamMember,
      active: false,
    }));

    // Then set the selected team member to active
    const index = updatedTeamMembers?.findIndex(
      teamMember => teamMember.id === selectedTeamMember.id,
    );

    if (index !== -1) {
      updatedTeamMembers[index] = {
        ...updatedTeamMembers[index],
        active: true,
      };
    }

    set({ teamMembers: updatedTeamMembers });
    set({ selectedTeamMember });
  },

  resetSelection: () => {
    set({
      selectedAccount: null,
      selectedContact: null,
      selectedStage: null,
      selectedTeamMember: null,
    });

    const accounts = get().accounts;
    const updatedAccounts = accounts.map(account => ({
      ...account,
      active: false,
    }));
    set({ accounts: updatedAccounts });

    const contacts = get().contacts;
    const updatedContacts = contacts.map(contact => ({
      ...contact,
      active: false,
    }));
    set({ contacts: updatedContacts });
    const stages = get().stages;
    const updatedStages = stages.map(stage => ({
      ...stage,
      active: false,
    }));
    set({ stages: updatedStages });

    const multiSelectContacts = get().multiSelectContacts;
    const updatedMultiSelectContacts = multiSelectContacts.map(contact => ({
      ...contact,
      active: false,
    }));
    set({ multiSelectContacts: updatedMultiSelectContacts });

    const teamMembers = get().teamMembers;
    const updatedTeamMembers = teamMembers.map(teamMember => ({
      ...teamMember,
      active: false,
    }));
    set({ teamMembers: updatedTeamMembers });
  },

  resetStageSelection: () => {
    const stages = get().stages;
    const updatedStages = stages.map(stage => ({
      ...stage,
      active: false,
    }));
    set({ stages: updatedStages });
    set({ selectedStage: null });
  },
  resetContactSelection: () => {
    const contacts = get().contacts;
    const updatedContacts = contacts.map(contact => ({
      ...contact,
      active: false,
    }));
    set({ contacts: updatedContacts });
    set({ selectedContact: null });
  },
  resetAccountSelection: () => {
    const accounts = get().accounts;
    const updatedAccounts = accounts.map(account => ({
      ...account,
      active: false,
    }));
    set({ accounts: updatedAccounts });
    set({ selectedAccount: null });
  },
  resetMultiSelectContactSelection: () => {
    const multiSelectContacts = get().multiSelectContacts;
    const updatedMultiSelectContacts = multiSelectContacts.map(contact => ({
      ...contact,
      active: false,
    }));
    set({ multiSelectContacts: updatedMultiSelectContacts });
    set({ selectedMultiSelectContact: null });
  },
  resetTeamMemberSelection: () => {
    const teamMembers = get().teamMembers;
    const updatedTeamMembers = teamMembers.map(teamMember => ({
      ...teamMember,
      active: false,
    }));
    set({ teamMembers: updatedTeamMembers });
    set({ selectedTeamMember: null });
  },
  resetActiveStateOfSelectTypeAttributes: () => {
    const selectTypeAttributes = get().selectTypeAttributes;

    const resetActiveState = (item: any): any => {
      // Base case: if item is an object with active property
      if (item && typeof item === "object") {
        const newItem = { ...item, active: false };

        // Handle arrays
        if (Array.isArray(item)) {
          return item.map(subItem => resetActiveState(subItem));
        }

        // Recursively process all nested properties
        Object.keys(newItem).forEach(key => {
          if (typeof newItem[key] === "object" && newItem[key] !== null) {
            newItem[key] = resetActiveState(newItem[key]);
          }
        });

        return newItem;
      }

      return item;
    };

    const updatedSelectTypeAttributes = selectTypeAttributes.map(attribute =>
      resetActiveState(attribute),
    );

    set({ selectTypeAttributes: updatedSelectTypeAttributes });
  },

  resetSelectTypeAttributes: (property_name: string) => {
    const selectTypeAttributes = get().selectTypeAttributes;
    const updatedSelectTypeAttributes = selectTypeAttributes.map(attribute =>
      attribute.property_name === property_name
        ? {
            ...attribute,
            options: attribute.options.map(option => ({
              ...option,
              active: false,
            })),
          }
        : attribute,
    );
    set({ selectTypeAttributes: updatedSelectTypeAttributes });
  },

  resetAllStates: () => {
    set({
      isConfirmClearForm: false,
      isConfirmCloseForm: false,
      addingNewAccount: false,
      selectedAccount: null,
      selectedContact: null,
      selectedStage: null,
      accounts: [],
      contacts: [],
      multiSelectContacts: [],
      selectTypeAttributes: [],
    });
  },
});
